import { useEffect, useRef } from 'react';

/** The keys the card prints: ⇧1–4 for the actions, ⇧5 onward for the drafts. */
const FIRST_KEY = 1;
const LAST_KEY = 7;

interface AssistantShortcuts {
  /** Whether the rows the keys name are on screen and pressable. */
  active: boolean;
  /** The digit pressed with shift, once every guard has passed. */
  onKey: (digit: number) => void;
}

/** Is this element one somebody is writing in? */
function isTyping(node: EventTarget | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.isContentEditable;
}

/** Is a dialog open over the card? */
function isDialogOpen(): boolean {
  return document.querySelector('[role="dialog"]') !== null;
}

/**
 * The card's keyboard layer.
 *
 * Matched on `event.code`, not `event.key`: Shift+1 is "!" on every layout this
 * ships to, so matching the character would mean keeping a table of punctuation
 * per keyboard. `code` is the physical key and says `Digit1` whatever is printed
 * on the cap.
 *
 * Three guards, and the first is the one that matters most. The composer sits
 * directly below this card, and an employer typing an exclamation mark into a
 * message must not reject a candidate — the most expensive way this feature
 * could be wrong. The second covers a dialog open over the card: EndChatDialog
 * is reachable from the header without collapsing it and traps no focus, so a
 * keystroke meant for its buttons would otherwise mark a hiring stage behind it.
 * The third is `active`, which the card sets false while it is collapsed or
 * still reading.
 *
 * The handler lives in a ref so the effect depends only on whether it should be
 * listening at all. The card re-renders on every message and every reading
 * transition, and rebinding a window listener on each of those is a leak waiting
 * to happen.
 */
export function useAssistantShortcuts({ active, onKey }: AssistantShortcuts): void {
  const handler = useRef<(event: KeyboardEvent) => void>(() => {});

  handler.current = (event: KeyboardEvent) => {
    if (!event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTyping(event.target)) return;
    if (isDialogOpen()) return;

    const match = /^Digit([1-9])$/.exec(event.code);
    if (!match) return;
    const digit = Number(match[1]);
    if (digit < FIRST_KEY || digit > LAST_KEY) return;

    // Nothing else on the page claims these, and letting the press through would
    // type "!" into whatever gains focus next.
    event.preventDefault();
    onKey(digit);
  };

  useEffect(() => {
    if (!active) return;
    const listener = (event: KeyboardEvent) => handler.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [active]);
}
